/**
 * Network Schemas - Browser-Safe Exports
 * 
 * This file contains Zod schemas for SIP Interface validation.
 * It is safe to import in browser environments (React, Vite, etc.)
 * 
 * CRITICAL: Do NOT import anything from:
 * - '../network/' (contains database operations)
 * - '../drizzle/' (database connection)
 * - Any other Node.js-only modules
 * 
 * @packageDocumentation
 */

import { z } from 'zod';
import { optionalEnum, optionalNumberInRange } from './transforms';

// ========================================
// TLS CONFIGURATION (Server-Mode / Inbound)
// ========================================

/**
 * TLS Configuration for SIP Interface Listeners (Server-Mode)
 * 
 * Different from OutboundTlsConfigSchema (in sip.ts) which is for client connections.
 * This schema handles server-side TLS where WE verify client certificates.
 * 
 * Key differences from OutboundTlsConfigSchema:
 * - Server-mode: verifyClient, requireClientCert, requestCert, honorCipherOrder
 * - Client-mode (sip.ts): verifyServer, sni
 */
export const InboundTlsConfigSchema = z.object({
  // Certificate configuration (required for TLS)
  certificateId: z.string().min(1, 'Certificate ID is required for TLS'),
  caCertificateId: z.string().optional(),
  
  // Client certificate behavior (server-mode specific)
  verifyClient: z.boolean().optional(),
  requireClientCert: z.boolean().optional(),
  requestCert: z.boolean().optional(),
  rejectUnauthorized: z.boolean().optional(),
  
  // TLS version control
  minVersion: optionalEnum(['TLSv1.2', 'TLSv1.3'] as const),
  maxVersion: optionalEnum(['TLSv1.2', 'TLSv1.3'] as const),
  
  // Cipher configuration (server controls cipher order)
  ciphers: z.array(z.string()).optional(),
  honorCipherOrder: z.boolean().optional(),
  
  // Teams compatibility (server-side for Direct Routing)
  teamsCompatible: z.boolean().optional(),
  
  // Session management - uses optionalNumberInRange to handle empty string/NaN from form inputs
  sessionTimeout: optionalNumberInRange(60, 86400),
  
  // Debugging
  enableTrace: z.boolean().optional(),
});

// ========================================
// NAT TRAVERSAL CONFIGURATION
// ========================================

/**
 * NAT Traversal Configuration (RFC 3581)
 * Controls symmetric response routing behavior per interface
 */
export const NatTraversalConfigSchema = z.object({
  // NAT traversal mode:
  // - 'auto': RFC 3581 compliant - add rport/received only when needed (default)
  // - 'always': Always add rport/received parameters (aggressive NAT handling)
  // - 'never': Disable NAT traversal (trusted internal networks only)
  mode: z.enum(['auto', 'always', 'never']).default('auto'),
  
  // Force rport even if client didn't request it
  forceRport: z.boolean().optional(),
});

// ========================================
// SIP INTERFACE SCHEMAS
// ========================================

/**
 * SIP Interface Create Input - SINGLE SOURCE OF TRUTH
 * 
 * Used by:
 * - Backend API: zValidator('json', SipInterfaceCreateInputSchema)
 * - Frontend: zodResolver(SipInterfaceCreateInputSchema)
 * 
 * This schema validates input for creating a new SIP interface.
 * Includes refinement to require TLS certificate when protocol is TLS.
 */
export const SipInterfaceCreateInputSchema = z.object({
  // Core identification
  interfaceId: z.string().min(1, 'Interface ID is required').max(64),
  
  // Network binding
  port: z.number().min(1024, 'Port must be >= 1024').max(65535, 'Port must be <= 65535'),
  protocol: z.enum(['UDP', 'TCP', 'TLS']),
  bindAddress: z.string().default('0.0.0.0'),
  advertisedAddress: z.string().nullable().optional(),
  hostId: z.string().uuid().nullable().optional(),
  tlsContextId: z.string().uuid().nullable().optional(),
  preClassificationManipulationId: z.string().uuid().nullable().optional(),
  
  // State
  enabled: z.boolean().default(true),
  
  // TLS configuration (required for TLS protocol, null for UDP/TCP)
  tlsConfig: InboundTlsConfigSchema.nullable().optional(),
  
  // NAT traversal configuration
  natTraversalConfig: NatTraversalConfigSchema.nullable().optional(),

  // OPTIONS behavior
  challengeUnmatchedOptions: z.boolean().default(false),
  
  // Capacity and limits - uses optionalNumberInRange to handle empty string/NaN from form inputs
  maxConnections: optionalNumberInRange(1, 100000),
  maxConcurrentCalls: optionalNumberInRange(1, 50000),
  
  // Metadata
  description: z.string().max(500).nullable().optional(),
  tags: z.array(z.string().max(50)).max(20).nullable().optional(),
}).refine(
  (data) => {
    // TLS protocol requires a certificate
    if (data.protocol === 'TLS') {
      return data.tlsConfig && data.tlsConfig.certificateId;
    }
    return true;
  },
  {
    message: 'TLS interfaces require a certificate ID',
    path: ['tlsConfig', 'certificateId'],
  }
);

/**
 * SIP Interface Update Input - SINGLE SOURCE OF TRUTH
 * 
 * All fields optional for partial updates.
 * Separate from Create because required fields differ.
 */
export const SipInterfaceUpdateInputSchema = z.object({
  // All fields optional for partial updates
  interfaceId: z.string().min(1).max(64).optional(),
  port: z.number().min(1024).max(65535).optional(),
  protocol: z.enum(['UDP', 'TCP', 'TLS']).optional(),
  bindAddress: z.string().optional(),
  advertisedAddress: z.string().nullable().optional(),
  hostId: z.string().uuid().nullable().optional(),
  tlsContextId: z.string().uuid().nullable().optional(),
  preClassificationManipulationId: z.string().uuid().nullable().optional(),
  enabled: z.boolean().optional(),
  
  // TLS configuration - nullable to accept null from frontend
  tlsConfig: InboundTlsConfigSchema.nullable().optional(),
  
  // NAT traversal configuration
  natTraversalConfig: NatTraversalConfigSchema.nullable().optional(),

  // OPTIONS behavior
  challengeUnmatchedOptions: z.boolean().optional(),
  
  // Capacity and limits - uses optionalNumberInRange to handle empty string/NaN from form inputs
  maxConnections: optionalNumberInRange(1, 100000),
  maxConcurrentCalls: optionalNumberInRange(1, 50000),
  
  // Metadata
  description: z.string().max(500).nullable().optional(),
  tags: z.array(z.string().max(50)).max(20).nullable().optional(),
});

// ========================================
// TYPE DEFINITIONS
// ========================================

/**
 * Type for inbound TLS configuration (server-mode)
 */
export type InboundTlsConfig = z.infer<typeof InboundTlsConfigSchema>;

/**
 * Type for NAT traversal configuration
 */
export type NatTraversalConfig = z.infer<typeof NatTraversalConfigSchema>;

/**
 * Type for creating a SIP Interface
 */
export type SipInterfaceCreateInput = z.infer<typeof SipInterfaceCreateInputSchema>;

/**
 * Type for updating a SIP Interface
 */
export type SipInterfaceUpdateInput = z.infer<typeof SipInterfaceUpdateInputSchema>;
